/* eslint-disable camelcase */
import { createCoreAxiosInstance } from 'createAxiosInstance';
import * as Sentry from '@sentry/react';
import { jwtDecode } from 'jwt-decode';
import { Cookies } from 'react-cookie';
import axios, { AxiosError } from 'axios';
import { notifications } from '@mantine/notifications';
import { COOKIES } from 'constants/auth';
import { ONE_DASHBOARD } from 'constants/integrations';
import { ACCESS, CUBOH_SUPPORT_EMAIL, REFRESH } from 'constants/cuboh';
import { logException } from './loggers';

const cookies = new Cookies();

/**
 * @param {string} password
 * @param {string} token
 * @returns {Promise<void>}
 */
export const confirmResetPassword = async (password, token) => {
  const axiosInstance = await createCoreAxiosInstance();

  await axiosInstance.post('/password_reset/validate_token/', { token });
  await axiosInstance.post('/password_reset/confirm/', { password, token });
};

export const updateUserMeta = async ({
  userMeta,
  failureMessage = 'Unable to update your preferences. Please try again later.',
}) => {
  try {
    const axiosInstance = await createCoreAxiosInstance();
    const { data } = await axiosInstance.patch('api/v2/user-meta', {
      user_meta: userMeta,
    });
    return data;
  } catch (error) {
    Sentry.captureException(error);
    notifications.show({
      title: failureMessage,
    });

    return Promise.reject(error);
  }
};
/**
 * Creates a valid path name for a given name,
 * used when creating the storefront route
 *
 * @example "Simba's Place 1" -> "simbas-place-1"
 *
 * @param name: The name to convert to a path
 * @returns URL path version of the name
 */
export const toPathName = name =>
  (name ?? '')
    .replace(/&/g, 'and')
    .replace(/[^A-Za-z0-9- ]/g, '')
    .replace(/-/g, ' ')
    .replace(/\s\s+/g, ' ')
    .replace(/\s/g, '-')
    .toLowerCase();

export const fetchFeatureFlags = async () => {
  try {
    const axiosInstance = await createCoreAxiosInstance();
    const { data } = await axiosInstance.get('/api/v2/feature-flags');
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(error.message);
  }
};

/* Feature Flag Helpers */
export const getShowMenuManagement = user =>
  !!user.userDetails?.userMeta?.enableMenuManagement || !!user.isParentCompanyUser;

export const getShowOrdersControlEmails = user =>
  !!user.userDetails?.userMeta?.disableOrdersControlEmails;

export const getShowOrdersDisplay = user => !!user.userDetails?.userMeta?.enableOrdersDisplay;

export const getShowStoreFront = user => user.isParentCompanyUser;

export const getShowMenuMapping = user => !!user.userDetails?.userMeta?.useV2Mapping;

export const getShowInvoices = user => {
  const hideInvoices = user.userDetails?.userMeta?.hideInvoices;
  if (hideInvoices === undefined) {
    return true;
  }
  return !hideInvoices;
};

export const getHideLiveChat = user => {
  const hideLiveChat = user.userDetails?.userMeta?.hideLiveChat;
  if (hideLiveChat === undefined) {
    return true;
  }
  return !hideLiveChat;
};
export const getShowDefaultModifiers = user => {
  const hideDefaultModifiers = user.userDetails?.userMeta?.hideDefaultModifiers;
  if (hideDefaultModifiers === undefined) {
    return true;
  }
  return !hideDefaultModifiers;
};
export const getAllowMultipleModifiers = user => {
  const allowMultipleModifiers = user.userDetails?.userMeta?.allowMultipleModifiers;
  if (allowMultipleModifiers === undefined) {
    return false;
  }
  return allowMultipleModifiers;
};

export const getAllowIndividualMenuPlatformPush = user =>
  user.userDetails?.userMeta?.allowIndividualMenuPlatformPush ?? false;

export const getShowRefreshPOS = user => !!user.userDetails?.userMeta?.enableRefreshPOS;

export const getPublishLocked = user => !!user.userDetails?.userMeta?.publishLocked ?? false;

export const formatAuthData = data => {
  const companies = data.companies.filter(
    company => company.merchants.length > 0 && company.active,
  );

  const payload = {
    chownowUser: {
      id: data.id,
      first_name: data.firstName,
      last_name: data.lastName,
      is_active: true,
      is_super_admin: false,
      is_region_admin: true,
      is_chownow_admin: false,
      is_cuboh_user: true,
      phone: '',
      group_id: '',
      company_id: null,
      email: data.email,
    },
    cubohUser: {
      id: data.userID,
      username: data.username,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      intercomHmac: data.intercomHmac,
      intercomID: data.intercomID,
      permissions: data.permissions,
      tokenClaims: data.tokenClaims,
      isAdmin: data.isAdmin,
      isStaff: data.isStaff,
      companyID: data.companyID,
      userDetails: data.userDetails,
      created: data.created,
      isParentCompanyUser: data.parentCompanyUserID === data.userID,
    },
    merchants: companies.reduce((accumulator, company) => {
      if (company.active) {
        company.merchants.forEach(merchant => {
          accumulator[merchant.id] = {
            ...merchant,
            companyID: company.id,
            storefrontPath: toPathName(merchant.name),
          };
        });
      }
      return accumulator;
    }, {}),
    companies: companies
      .map(company => {
        // eslint-disable-next-line no-param-reassign
        delete company.merchants;
        return company;
      })
      .reduce(
        (accumulator, company) => ({
          ...accumulator,
          [company.id]: {
            ...company,
            storefrontPath: toPathName(company.name),
          },
        }),
        {},
      ),
    parentCompany: {
      id: data.parentCompanyID ?? data.company_parentID,
      name: data.parentCompanyName,
      userID: data.parentCompanyUserID,
    },
    selectedTimezone: data.companyTimezone ?? data.companies[0].companyTimezone ?? 'US/Eastern',
    proxyUserID: data.tokenClaims.proxy_user === undefined ? null : data.tokenClaims.proxy_user,
    selectedCompanyID: companies[0].id,
    selectedMerchantID: null,
  };

  payload.cubohUser.flags = {
    SHOW_MENU_MANAGEMENT: getShowMenuManagement(payload.cubohUser),
    SHOW_ORDERS_DISPLAY: getShowOrdersDisplay(payload.cubohUser),
    SHOW_MENU_MAPPING: getShowMenuMapping(payload.cubohUser),
    SHOW_INVOICES: getShowInvoices(payload.cubohUser),
    SHOW_LIVE_CHAT: getHideLiveChat(payload.cubohUser),
    SHOW_DEFAULT_MODIFIERS: getShowDefaultModifiers(payload.cubohUser),
    ALLOW_MULTIPLE_MODIFIERS: getAllowMultipleModifiers(payload.cubohUser),
    ALLOW_INDIVIDUAL_PUBLISHES: getAllowIndividualMenuPlatformPush(payload.cubohUser),
    DISABLE_ORDERS_CONTROL_EMAILS: getShowOrdersControlEmails(payload.cubohUser),
    PUBLISH_LOCKED: getPublishLocked(payload.cubohUser),
    SHOW_MENU_EXPORT: !!payload.cubohUser.userDetails?.userMeta?.showMenuExport,
  };

  return payload;
};

export const fetchUserData = async () => {
  try {
    const axiosInstance = await createCoreAxiosInstance();
    const {
      data: { response },
    } = await axiosInstance.get('/api/v2/user');
    if (response.companies.length < 1) {
      const errorMessage = `User is no longer active - please contact ${CUBOH_SUPPORT_EMAIL} for further assistance.`;
      throw Error(errorMessage);
    }

    return formatAuthData(response);
  } catch (error) {
    if (error.response?.data?.code === 'user_inactive') {
      return logException({ message: 'Attempted to load inactive Cuboh user' });
    }
    Sentry.captureException(error);
    throw new Error(error.message);
  }
};

export const fetchUserDataByCnRestaurantID = async restaurantId => {
  try {
    const axiosInstance = await createCoreAxiosInstance();
    const {
      data: { response },
    } = await axiosInstance.get(`/api/chownow/admin/restaurant/${restaurantId}/`);
    if (response.companies.length < 1) {
      const errorMessage = `ChowNow restaurantId=${restaurantId} has no corresponding Cuboh company`;
      throw Error(errorMessage);
    }

    return formatAuthData(response);
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(error.message);
  }
};
export const fetchCubohUserDataByCnHq = async hqId => {
  try {
    const axiosInstance = await createCoreAxiosInstance();
    const {
      data: { response },
    } = await axiosInstance.get(`/api/chownow/admin/hq/${hqId}/`);
    if (response.companies.length < 1) {
      const errorMessage = `ChowNow hqID=${hqId} has no corresponding Cuboh companies`;
      throw Error(errorMessage);
    }

    return formatAuthData(response);
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(error.message);
  }
};
/**
 * Returns the parseInt of a given value
 *
 * Throws TypeError if value is null, undefined or isNaN
 */

export const formatCompanyID = id => {
  const parsedID = parseInt(id, 10);
  if (Number.isNaN(parsedID)) throw TypeError('Provided ID is not a number');
  return parsedID;
};

/**
 * Returns the parseInt of a given value
 * Returns the null if the given value is null, undefined or isNaN
 */

export const formatMerchantID = id => {
  const parsedID = parseInt(id, 10);
  if (Number.isNaN(parsedID)) return null;
  return parsedID;
};

export const tokenIsIssuedByChowNow = token => {
  try {
    const decodedToken = jwtDecode(token);
    if (decodedToken.iss && decodedToken.iss === 'chownow') return true;
  } catch (error) {
    Sentry.captureException(error);
  }
  return false;
};

export const getTokensFromCookie = () => ({
  authAccessToken: cookies.get(COOKIES.AUTH_ACCESS_TOKEN),
  authRefreshToken: cookies.get(COOKIES.AUTH_REFRESH_TOKEN),
  reportingAccessToken: cookies.get(COOKIES.REPORTING_ACCESS_TOKEN),
});

export const getAccessToken = () => {
  const { authAccessToken } = getTokensFromCookie();
  if (authAccessToken) return authAccessToken;
  return window.localStorage.getItem(ACCESS);
};

export const getRefreshToken = () => {
  const { authRefreshToken } = getTokensFromCookie();
  if (authRefreshToken) return authRefreshToken;
  return window.localStorage.getItem(REFRESH);
};

function getRefreshTokenExpiry() {
  try {
    const rawRefreshToken = getRefreshToken();
    // rawRefreshToken = "[encoded type].[encoded payload]"
    // refreshToken = { ... "exp": [number] }
    return JSON.parse(atob(rawRefreshToken?.split('.')[1]))?.exp;
  } catch {
    // do nothing
  }

  return undefined;
}

export const getReportingToken = () => {
  const { reportingAccessToken } = getTokensFromCookie();
  return reportingAccessToken;
};

// eslint-disable-next-line no-confusing-arrow
export const getAuthorizationHeader = () =>
  getAccessToken() ? `Bearer ${getAccessToken()}` : null;

export const tokenHasCubohUserId = token => {
  if (!token) return false;
  try {
    const decodedToken = jwtDecode(token);
    if (decodedToken?.user_id) return true;
  } catch (error) {
    Sentry.captureException(error);
  }
  return false;
};

export const verifyToken = async (token = getAccessToken()) => {
  const axiosInstance = await createCoreAxiosInstance();
  try {
    await axiosInstance.post('/api/v2/token/verify', { token });
    return true;
  } catch (error) {
    return false;
  }
};

export const resetPassword = async email => {
  const axiosInstance = await createCoreAxiosInstance();
  await axiosInstance.post('/password_reset/', { email, source: ONE_DASHBOARD });
};

export const getValidCubohCompanyID = ({
  isUserCubohOnly,
  singleSelectedCompanyID,
  location,
  companies,
}) => {
  if (isUserCubohOnly) {
    return singleSelectedCompanyID || (Object.keys(companies).length && Object.keys(companies)[0]);
  }

  if (!location?.cuboh?.restaurant_id) {
    return null;
  }

  const validLocationID = companies[location?.cuboh?.restaurant_id]?.id;

  return validLocationID;
};

export const getLatestTokens = async () => {
  try {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      throw new Error('cannot refresh tokens without a refresh token');
    }

    const { data } = await axios.post(import.meta.env.VITE_CHOWNOW_JWT_REFRESH_URL, {
      refresh_token: refreshToken,
    });
    cookies.set(COOKIES.AUTH_ACCESS_TOKEN, data.access_token, {
      path: '/',
      domain: import.meta.env.VITE_COOKIE_DOMAIN,
    });
    cookies.set(COOKIES.REPORTING_ACCESS_TOKEN, data.reporting_token, {
      path: '/',
      domain: import.meta.env.VITE_COOKIE_DOMAIN,
    });

    return {
      accessToken: data.access_token,
      refreshToken,
      reportingToken: data.reporting_token,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const expiry = getRefreshTokenExpiry();

      Sentry.captureException(
        new Error('failed to refresh tokens'),
        typeof expiry === 'number'
          ? { extra: { token_is_expired: expiry < Date.now() / 1000 } }
          : undefined,
      );
    } else {
      Sentry.captureException(error);
    }
  }

  return {
    accessToken: undefined,
    refreshToken: undefined,
    reportingToken: undefined,
  };
};
